export type PermissionRole = "tenant_admin" | "supervisor" | "agent";

export interface PermissionItem {
  label: string;
  description?: string;
  roles: PermissionRole[];
  featureFlagged?: boolean;
}

export interface PermissionSection {
  section: string;
  featureFlagged?: boolean;
  items: PermissionItem[];
}

export const PERMISSION_MATRIX: PermissionSection[] = [
  {
    section: "General",
    items: [
      { label: "Dashboard", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Custom Dashboards", roles: ["tenant_admin", "supervisor"] },
      { label: "Call History", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "View All Call History", description: "When enabled, agents can see all calls across the tenant — not just their own.", roles: ["tenant_admin", "supervisor"] },
      { label: "Download Recordings", roles: ["tenant_admin"], featureFlagged: true },
      { label: "Voicemails", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Knowledge Base", roles: ["tenant_admin", "supervisor", "agent"], featureFlagged: true },
      { label: "My Profile", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "AI Assistant", description: "When enabled, the AI Assistant button is visible in the header.", roles: ["tenant_admin", "supervisor", "agent"] },
    ],
  },
  {
    section: "Conversations",
    items: [
      { label: "Active Conversations", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Inbox / History", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "View All Conversations", roles: ["tenant_admin", "supervisor"] },
      { label: "Conversation Dashboard", roles: ["tenant_admin", "supervisor"] },
      { label: "Conversation Templates", roles: ["tenant_admin", "supervisor"] },
      { label: "Team Chat", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Reassign Conversations", roles: [] },
      { label: "Live Chat Settings", roles: ["tenant_admin"] },
      { label: "WhatsApp Settings", roles: ["tenant_admin"] },
      { label: "Facebook Settings", roles: ["tenant_admin"] },
      { label: "Instagram Settings", roles: ["tenant_admin"] },
    ],
  },
  {
    section: "CRM",
    items: [
      { label: "Pipeline", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "My Activities", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Activity Tracking", roles: ["tenant_admin", "supervisor"] },
      { label: "Contacts", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Contact Lists", roles: ["tenant_admin", "supervisor"] },
      { label: "DNC Lists", roles: ["tenant_admin", "supervisor"] },
      { label: "Blocked Numbers", roles: ["tenant_admin", "supervisor"] },
      { label: "Automations", roles: ["tenant_admin", "supervisor"], featureFlagged: true },
      { label: "Pipeline Settings", roles: ["tenant_admin"] },
    ],
  },
  {
    section: "Ticketing",
    featureFlagged: true,
    items: [
      { label: "Tickets", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Ticket History", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Ticket Dashboard", roles: ["tenant_admin", "supervisor"] },
      { label: "Ticket Templates", roles: ["tenant_admin", "supervisor"] },
      { label: "Ticketing Settings", roles: ["tenant_admin"] },
    ],
  },
  {
    section: "CSAT Surveys",
    featureFlagged: true,
    items: [
      { label: "CSAT Surveys", roles: ["tenant_admin", "supervisor"] },
      { label: "Survey Results", roles: ["tenant_admin", "supervisor"] },
      { label: "CSAT Dashboard", roles: ["tenant_admin", "supervisor"] },
      { label: "CSAT Settings", roles: ["tenant_admin", "supervisor"] },
    ],
  },
  {
    section: "Power Dialer",
    featureFlagged: true,
    items: [
      { label: "Dashboard", roles: ["tenant_admin", "supervisor"] },
      { label: "Campaigns", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Workflows", roles: ["tenant_admin", "supervisor"] },
    ],
  },
  {
    section: "Preview Dialer",
    featureFlagged: true,
    items: [
      { label: "Dashboard", roles: ["tenant_admin", "supervisor"] },
      { label: "Campaigns", roles: ["tenant_admin", "supervisor", "agent"] },
      { label: "Dialer Scripts", roles: ["tenant_admin", "supervisor"] },
    ],
  },
  {
    section: "SMS Campaigns",
    featureFlagged: true,
    items: [
      { label: "SMS Campaigns", roles: ["tenant_admin", "supervisor"] },
    ],
  },
  {
    section: "Screen Pop",
    items: [
      { label: "Script Manager", roles: ["tenant_admin", "supervisor"] },
      { label: "Settings", roles: ["tenant_admin"] },
      { label: "External Links", roles: ["tenant_admin"] },
    ],
  },
  {
    section: "Settings",
    items: [
      { label: "Reports", roles: ["tenant_admin", "supervisor"] },
      { label: "Notifications", roles: ["tenant_admin", "supervisor"], featureFlagged: true },
      { label: "Dispositions", roles: ["tenant_admin", "supervisor"] },
      { label: "Queues", roles: ["tenant_admin", "supervisor"] },
      { label: "Phone Numbers", roles: ["tenant_admin"] },
      { label: "IVR Menus", roles: ["tenant_admin"] },
      { label: "Extensions", roles: ["tenant_admin"] },
      { label: "Recordings", roles: ["tenant_admin"] },
      { label: "Business Hours", roles: ["tenant_admin"] },
      { label: "General Settings", roles: ["tenant_admin"] },
      { label: "Agent Statuses", roles: ["tenant_admin"] },
      { label: "Billing", roles: ["tenant_admin"] },
      { label: "Webhooks", roles: ["tenant_admin"] },
      { label: "API Keys", roles: ["tenant_admin"] },
      { label: "Integrations", roles: ["tenant_admin"] },
      { label: "SIP Forwarding", roles: ["tenant_admin"], featureFlagged: true },
      { label: "Custom Caller IDs", roles: ["super_admin" as any], featureFlagged: true },
      { label: "API Docs", roles: ["tenant_admin"] },
      { label: "AI Bot", roles: ["tenant_admin"] },
      { label: "Audit Log", roles: ["tenant_admin"] },
    ],
  },
  {
    section: "Administration",
    items: [
      { label: "Users", roles: ["tenant_admin"] },
      { label: "Workforce", roles: ["tenant_admin", "supervisor"] },
      { label: "Permissions", roles: ["tenant_admin"] },
    ],
  },
];

export const EDITABLE_ROLES: PermissionRole[] = ["supervisor", "agent"];

export const ROLE_LABELS: Record<PermissionRole, string> = {
  tenant_admin: "Tenant Admin",
  supervisor: "Supervisor",
  agent: "Agent",
};

export function getDefaultAllowed(section: string, label: string, role: PermissionRole): boolean {
  const sec = PERMISSION_MATRIX.find(s => s.section === section);
  const item = sec?.items.find(i => i.label === label);
  return item?.roles.includes(role) ?? false;
}
